import React, { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  mapBankTransactionFilterField,
  mapBankTransactionSortField,
} from "../../api/filters/bankTransactionFilters";
import { PREDEFINED_BANK_TRANSACTION_FILTERS } from "../../status";
import type { BackendFilters } from "../../utils/backendFilters";
import useDevMode from "../../utils/devmode";
import { sortAlphabetically, toValueOptions } from "../../utils/utils";
import DataTable, {
  type PaginationModel,
  type SortModel,
  useServerFilterModel,
} from "../DataTable";
import CustomNoRowsOverlay from "../TableNoDataOverlay";
import { getDefaultColumns } from "./columns";

const EMPTY_STATE_CONTENT: Record<
  PREDEFINED_BANK_TRANSACTION_FILTERS,
  { title: string; description: string }
> = {
  [PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW]: {
    title: "You're all caught up",
    description: "No transactions need review right now.",
  },
  [PREDEFINED_BANK_TRANSACTION_FILTERS.PENDING]: {
    title: "Nothing pending",
    description: "No transactions are waiting for processing.",
  },
  [PREDEFINED_BANK_TRANSACTION_FILTERS.ALL]: {
    title: "No transactions yet",
    description: "Transactions will appear here once bank data is imported.",
  },
};

const PINNING = { start: ["practiceDisplayName"], end: ["status", "unmatch"] };
const INITIAL_COLUMN_VISIBILITY = {
  wieldyMerchantName: false,
  payerLabel: false,
};

interface ReconciliationsTableProps {
  isLoading: boolean;
  transactions: BankTransactionMessage[];
  updateTransaction: (
    transactionUpdate: BankTransactionUpdateMessage,
  ) => Promise<void>;
  onFindMatches?: (transaction: BankTransactionMessage) => void;
  onUnmatchTransaction?: (wieldyId: string) => Promise<void>;
  persistKey: string;
  selectedFilter: PREDEFINED_BANK_TRANSACTION_FILTERS;
  onFilterChange?: (filters: BackendFilters) => void;
  onSortChange?: (field: string, direction: "asc" | "desc") => void;
  page: number;
  pageSize: number;
  onPageChange?: (page: number, pageSize: number) => void;
  rowCount?: number;
  pendingIds?: Set<string>;
}

export default function ReconciliationsTable({
  isLoading,
  transactions,
  updateTransaction,
  onFindMatches,
  onUnmatchTransaction,
  persistKey,
  selectedFilter,
  onFilterChange,
  onSortChange,
  page,
  pageSize,
  onPageChange,
  rowCount,
  pendingIds,
}: ReconciliationsTableProps) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const devMode = useDevMode();

  const { filterModel, onFilterModelChange } = useServerFilterModel({
    persistKey,
    mapField: mapBankTransactionFilterField,
    onFiltersChange: onFilterChange,
  });
  const [sortModel, setSortModel] = useState<SortModel>([
    { field: "bankDate", sort: "desc" },
  ]);

  // The search endpoint sorts by one field, so only the primary key is sent.
  const handleSortModelChange = useCallback(
    (model: SortModel) => {
      setSortModel(model);
      if (onSortChange && model.length > 0) {
        const { field, sort } = model[0];
        onSortChange(mapBankTransactionSortField(field), sort);
      }
    },
    [onSortChange],
  );

  const handlePaginationModelChange = useCallback(
    (model: PaginationModel) => onPageChange?.(model.page + 1, model.pageSize),
    [onPageChange],
  );

  const columns = getDefaultColumns(
    toValueOptions(
      transactions.map((t) => ({ practiceDisplayName: t.practiceDisplayName })),
      "practiceDisplayName",
      sortAlphabetically,
    ),
    searchParams,
    updateTransaction,
    onFindMatches,
    onUnmatchTransaction,
    devMode,
    selectedFilter,
    pendingIds,
  );

  const columnVisibilityModel = React.useMemo(
    () =>
      selectedFilter === PREDEFINED_BANK_TRANSACTION_FILTERS.REVIEW
        ? { monetId: true, paymentKey: false, matchedAt: false }
        : { monetId: false, paymentKey: true, matchedAt: false },
    [selectedFilter],
  );

  return (
    <DataTable
      loading={isLoading}
      persistKey={persistKey}
      rows={transactions}
      getRowId={(row) => row.wieldyId}
      columns={columns}
      rowCount={rowCount ?? transactions.length}
      paginationModel={{ page: page - 1, pageSize }}
      pageSizeOptions={[10, 20, 40]}
      onPaginationModelChange={handlePaginationModelChange}
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      initialColumnVisibility={INITIAL_COLUMN_VISIBILITY}
      columnVisibilityOverrides={columnVisibilityModel}
      initialPinning={PINNING}
      getRowClassName={(row) =>
        pendingIds?.has(row.wieldyId) ? "monet-row-pending" : ""
      }
      noRowsOverlay={
        <CustomNoRowsOverlay
          title={EMPTY_STATE_CONTENT[selectedFilter].title}
          description={EMPTY_STATE_CONTENT[selectedFilter].description}
        />
      }
      sx={(theme) => ({
        "& .monet-row-pending": {
          backgroundColor: theme.palette.action.hover,
          transition: theme.transitions.create(
            ["background-color", "opacity"],
            { duration: theme.transitions.duration.short },
          ),
        },
        "& .monet-row-pending .DataTable-cell": {
          opacity: 0.85,
        },
      })}
    />
  );
}
